import { app } from "modular-openscriptjs";
import { CapacitorHttp } from "@capacitor/core";

/**
 * Short reference to Object.prototype.hasOwnProperty.
 */
export const { hasOwnProperty } = Object.prototype;

/**
 * Checks if the given value is an object.
 * @param {*} value - The value to check.
 * @returns {boolean} Returns `true` if the given value is an object, else `false`.
 */
export function isObject(value) {
    return typeof value === "object" && value !== null;
}

/**
 * Checks if the given value is a plain object.
 * @param {*} value - The value to check.
 * @returns {boolean} Returns `true` if the given value is a plain object, else `false`.
 */
export function isPlainObject(value) {
    // This solution aligns with lodash, jQuery, redux implementations
    if (!isObject(value)) return false;

    // Accept objects with null prototype
    // Object.create(null)
    const proto = Object.getPrototypeOf(value);
    if (proto === null) return true;

    try {
        const { constructor } = value;
        const { prototype } = constructor;
        return (
            constructor &&
            prototype &&
            hasOwnProperty.call(prototype, "isPrototypeOf")
        );
    } catch {
        return false;
    }
}

/**
 * The Doc Class provides shorted API for the document model that is available in the windows object.
 */
export class dom {
    /**
     * Returns a new DOM object. Add this to the window using `window.dom = new Dom()`;
     */
    constructor() {}

    /**
     * Helper method for dispatching custom events
     */
    static fire(eventName, data = null) {
        const event = new CustomEvent(eventName, {
            detail: data,
            bubbles: true,
            cancelable: true,
        });
        document.dispatchEvent(event);
    }

    /**
     * @returns document.documentElement.clientWidth
     */
    static vw() {
        return document.documentElement.clientWidth;
    }

    /**
     * @returns document.documentElement.clientHeight
     */
    static vh() {
        return document.documentElement.clientHeight;
    }

    /**
     * @returns document.documentElement.dataset
     */
    static data() {
        return document.documentElement.dataset;
    }

    /**
     * @returns document.documentElement
     */
    static el() {
        return document.documentElement;
    }

    /**
     * ***document.forms.namedItem(name)***
     * @param {String} name HTML form id or name
     * @returns
     */
    static form(name) {
        return document.forms.namedItem(name);
    }

    /**
     * ***form.elements.namedItem(name)***
     * @param {String} name HTML form element id or name
     * @param {String|HTMLFormElement} form HTML form id or name | HTMLFormElement
     * @returns
     */
    static formEl(name, form) {
        if (form instanceof HTMLFormElement) {
            return form.elements.namedItem(name);
        }
        if (typeof form === "string" || form instanceof String) {
            const formEl = document.forms.namedItem(form);
            if (formEl) {
                return formEl.elements.namedItem(name);
            }
        }
        return null;
    }

    /**
     * Safely sets the `.value` property of the element matched by `selector`.
     * @param {string} selector CSS selector
     * @param {*} value
     * @param {HTMLElement} parent parent element
     */
    static val(selector, value, parent = null) {
        const el = this.get(selector, parent);
        if (el) {
            if (el instanceof HTMLSelectElement && el.multiple) {
                const cleanValue = Array.isArray(value) ? value : [value];
                Array.from(el.options).forEach((opt) => {
                    opt.selected = cleanValue.includes(opt.value);
                });
            } else {
                el.value = value ?? "";
            }
        }
    }

    /**
     * Safely sets the `.checked` property of the element matched by `selector`.
     * @param {string} selector CSS selector
     * @param {boolean} value
     * @param {HTMLElement} parent parent element
     */
    static chk(selector, value, parent = null) {
        const el = this.get(selector, parent);
        if (el) el.checked = Boolean(value ?? false);
    }

    /**
     * Safely sets the `.value` property of a HTML form element
     * @param {string} name HTML form element id or name
     * @param {String|HTMLFormElement} form HTML form id or name | HTMLFormElement
     * @param {*} value
     */
    static formVal(name, form, value) {
        const el = this.formEl(name, form);
        if (el) {
            if (el instanceof HTMLSelectElement && el.multiple) {
                const cleanValue = Array.isArray(value) ? value : [value];
                Array.from(el.options).forEach((opt) => {
                    opt.selected = cleanValue.includes(opt.value);
                });
            } else {
                el.value = value ?? "";
            }
        }
    }

    /**
     * Safely sets the `.checked` property of a HTML form element
     * @param {string} name HTML form element id or name
     * @param {String|HTMLFormElement} form HTML form id or name | HTMLFormElement
     * @param {boolean} value
     */
    static formChk(name, form, value) {
        const el = this.formEl(name, form);
        if (el) el.checked = Boolean(value ?? false);
    }

    /**
     * Gets a single element
     * @param {string} selector css selector
     * @param {HTMLElement} parent defaults to document
     */
    static get(selector, parent = null) {
        if (!parent) parent = document;
        return parent.querySelector(selector);
    }

    /**
     * Gets all the elements
     * @param {string} selector css selector
     * @param {HTMLElement} parent defaults to document
     * @returns
     */
    static all(selector, parent = null) {
        if (!parent) parent = document;
        return parent.querySelectorAll(selector);
    }

    /**
     * Gets the first element from the selected node list
     * @param {string} selector css selector
     * @param {HTMLElement} parent defaults to document
     */
    static first(selector, parent = null) {
        let list = this.all(selector, parent);
        return list.length == 0 ? null : list[0];
    }

    /**
     * Gets the last element from the select node list
     * @param {string} selector css selector
     * @param {HTMLElement} parent defaults to document
     */
    static last(selector, parent = null) {
        let list = this.all(selector, parent);
        return list.length == 0 ? null : list[list.length - 1];
    }

    /**
     * Get element at a position
     * @param {string} selector css selector
     * @param {int} position
     * @param {HTMLElement} parent defaults to document
     * @returns
     */
    static at(selector, position, parent = null) {
        let list = this.all(selector, parent);
        return list.length == 0 ? null : list[position];
    }

    /**
     * Creates an HTML element
     * @param {string} elementType
     * @returns
     */
    static element(elementType) {
        return document.createElement(elementType);
    }

    /**
     * Puts an inner html in an element
     * @param {HTMLElement} element
     * @param {string} innerHTML
     * @param {bool} append append to current html?
     */
    static put(innerHTML, element, append = false) {
        if (append) {
            element.innerHTML += innerHTML;
            return;
        }

        element.innerHTML = innerHTML;
    }

    /**
     * ***document.getElementById(id)***
     * @param {string} id
     * @param {HTMLElement} parent defaults to document
     * @returns {HTMLElement|null}
     */
    static id(id, parent = null) {
        if (!parent) parent = document;

        return parent.getElementById(`${id}`);
    }

    /**
     * ***document.getElementsByClass(class)***
     * @param {string} className
     * @param {HTMLElement} parent defaults to document
     * @returns
     */
    static class(className, parent = null) {
        return dom.all(`.${className}`, parent);
    }

    /**
     * Sets innerHTML to empty string
     * @param {HTMLElement} element
     */
    static empty(element) {
        if (!element) return;
        element.innerHTML = "";
    }

    /**
     * Checks if the element has no innerHTML
     * @param {HTMLElement} element
     */
    static isEmpty(element) {
        if (element?.value) return element?.value.length < 1;

        return /^[\t\r\n\s]*$/g.test(element?.innerHTML);
    }

    /**
     * Disables an element
     * @param {HTMLElement} element
     */
    static disable(element) {
        element?.setAttribute("disabled", "true");
    }

    /**
     * Enables an element
     * @param {HTMLElement} element
     */
    static enable(element) {
        element.removeAttribute("disabled");
    }

    /**
     * Centers an absolutely positioned element (y) inside another (x),
     * regardless of where x is in the DOM.
     * @param {HTMLElement} x - The container element (e.g., #x)
     * @param {HTMLElement} y - The element to center (e.g., #y)
     */
    static centerInside(
        x,
        y,
        useOffset = true,
        adjustLeft = null,
        adjustTop = null,
    ) {
        if (!x || !y) {
            console.warn("Both x and y elements must be provided");
            return;
        }

        let top = 0;
        let left = 0;

        if (useOffset) {
            top = x.offsetTop + x.offsetHeight / 2;
            left = x.offsetLeft + x.offsetWidth / 2;
        } else {
            const xRect = x.getBoundingClientRect();

            // x's position relative to the page
            const xTop = xRect.top;
            const xLeft = xRect.left;

            // Compute center position relative to x's top-left corner
            top = xTop + xRect.height / 2;
            left = xLeft + xRect.width / 2;
        }

        if (adjustLeft) left += adjustLeft;
        if (adjustTop) top += adjustTop;

        y.style.top = `${top}px`;
        y.style.left = `${left}px`;
    }
}

export class Offcanvas {
    static duration = 300; // Transition duration (in milliseconds)
    static types = ["bottom", "start", "end", "top"];
    static zIndex = 2000;

    /**
     * Stores the currently open offcanvas metadata
     */
    static active = null;

    static {
        if (!dom.data().wabOffcanvasInitialized) {
            dom.data().wabOffcanvasInitialized = "true";

            // Global Escape key listener
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape" && Offcanvas.active) {
                    if (Offcanvas.active.closeable === true) {
                        Offcanvas.close(Offcanvas.active.id);
                    }
                }
            });

            // Global Backdrop click listener
            document.addEventListener("click", (e) => {
                if (e.target.classList.contains("wab-offcanvas-backdrop")) {
                    const id = e.target.id;
                    if (
                        id &&
                        Offcanvas.active &&
                        Offcanvas.active.id === id &&
                        Offcanvas.active.closeable === true
                    ) {
                        Offcanvas.close(id);
                    }
                }
            });
        }
    }

    /**
     * Open offcanvas
     * @param {String} id - The ID of the offcanvas to open.
     * @param {Object} options - Options for opening the offcanvas.
     */
    static open(
        id,
        {
            focus = true,
            closeable = true,
            data = null,
            duration = Offcanvas.duration,
        } = {},
    ) {
        const backdrop = dom.id(id);
        if (!backdrop || !backdrop.classList.contains("wab-offcanvas-backdrop"))
            return;

        // Close any currently open offcanvas first
        if (Offcanvas.active && Offcanvas.active.id !== id) {
            Offcanvas.close(Offcanvas.active.id);
        }

        dom.fire("wab.offcanvas.open", { id, data });

        Offcanvas.active = { id, focus, closeable, duration, data };
        backdrop.style.zIndex = Offcanvas.zIndex;

        const offcanvas = backdrop.querySelector(".wab-offcanvas");
        if (duration !== Offcanvas.duration) {
            backdrop.style.transition = `opacity ${duration}ms ease, visibility ${duration}ms ease`;
            offcanvas.style.transition = `transform ${duration}ms ease`;
        }

        backdrop.classList.add("wab-offcanvas-open");
        document.body.style.overflow = "hidden";

        setTimeout(() => {
            if (focus) {
                const target = offcanvas.querySelector("[autofocus]");
                if (target) {
                    target.focus();
                } else {
                    const focusable = offcanvas.querySelectorAll(
                        'input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])',
                    );
                    if (focusable.length > 0) focusable[0].focus();
                }
            }
            dom.fire("wab.offcanvas.opened", { id, data });
        }, duration);
    }

    /**
     * Close open offcanvas
     */
    static close(id, meta = null) {
        const backdrop = dom.id(id);
        if (!Offcanvas.active || Offcanvas.active.id !== id) return;

        // Merge metadata
        Offcanvas.active["data"] = {
            ...Offcanvas.active["data"],
            ...{ __close__: meta },
        };

        const { duration, data } = Offcanvas.active;
        dom.fire("wab.offcanvas.close", { id, data });
        if (backdrop) backdrop.classList.remove("wab-offcanvas-open");

        document.body.style.overflow = "";

        // Cleanup metadata, fire closed event
        const doCleanup = () => {
            Offcanvas.active = null;
            dom.fire("wab.offcanvas.closed", { id, data });
        };

        if (backdrop) {
            setTimeout(doCleanup, duration);
        } else {
            doCleanup();
        }
    }

    /**
     * Retrieve data passed to an open offcanvas
     */
    static getData(id) {
        return Offcanvas.active && Offcanvas.active.id === id
            ? Offcanvas.active.data
            : null;
    }
}

export const offcanvasTypes = Offcanvas.types;

export function openOffcanvas(id, options) {
    Offcanvas.open(id, options);
}

export function closeOffcanvas(id, meta) {
    Offcanvas.close(id, meta);
}

export class Dropdown {
    static duration = 300; // Transition duration (in milliseconds)
    static types = ["dropdown", "dropup", "dropstart", "dropend"];
    static anchors = ["start", "end"];

    static {
        if (!dom.data().wabDropdownInitialized) {
            dom.data().wabDropdownInitialized = "true";

            document.addEventListener("click", (event) => {
                if (!dom.data().wabDropdownOpen) return;
                const dropdown = dom.id(dom.data().wabDropdownOpen);
                if (!dropdown || dropdown.contains(event.target)) return;
                Dropdown.closeDropdown(dropdown.id);
            });
        }
    }

    static closeDropdown(dropdownId) {
        const dropdown = dom.id(dropdownId);
        if (!dropdown) return;
        dropdown.classList.remove("wab-dropdown-open");
        setTimeout(() => {
            dropdown.style.cssText = "";
            dom.el().removeAttribute("data-wab-dropdown-open");
        }, Dropdown.duration);
    }

    /**
     * Check if dropdown fits within the viewport given the position
     * @param {Object} pos top, right, bottom, left values to try on dropdown
     * @param {Number} dw dropdown width
     * @param {Number} dh dropdown height
     * @returns
     */
    static checkFit(pos, dw, dh) {
        const vw = dom.vw();
        const vh = dom.vh();

        let top = parseFloat(pos.top);
        let left = parseFloat(pos.left);
        let bottom = parseFloat(pos.bottom);
        let right = parseFloat(pos.right);

        // If top is auto and bottom is not
        if (isNaN(top) && !isNaN(bottom)) {
            top = vh - bottom - dh;
        }

        // If left is auto and right is not
        if (isNaN(left) && !isNaN(right)) {
            left = vw - right - dw;
        }

        return top >= 0 && left >= 0 && top + dh <= vh && left + dw <= vw;
    }

    /**
     * Get the relative position options for the dropdown
     * @param {DOMRect} rect rect of the dropdown toggle element
     * @param {Number} offset block offset for dropdown/dropup, inline offset for dropstart/dropend
     * @returns
     */
    static relativePositionOptions(rect, offset = 4) {
        return {
            dropdown: {
                start: {
                    top: rect.bottom + offset + "px",
                    right: "auto",
                    bottom: "auto",
                    left: rect.left + "px",
                },
                end: {
                    top: rect.bottom + offset + "px",
                    right: dom.vw() - rect.right + "px",
                    bottom: "auto",
                    left: "auto",
                },
            },
            dropup: {
                start: {
                    top: "auto",
                    right: "auto",
                    bottom: dom.vh() - rect.top + offset + "px",
                    left: rect.left + "px",
                },
                end: {
                    top: "auto",
                    right: dom.vw() - rect.right + "px",
                    bottom: dom.vh() - rect.top + offset + "px",
                    left: "auto",
                },
            },
            dropstart: {
                start: {
                    top: rect.top + "px",
                    right: dom.vw() - rect.left + offset + "px",
                    bottom: "auto",
                    left: "auto",
                },
                end: {
                    top: "auto",
                    right: dom.vw() - rect.left + offset + "px",
                    bottom: dom.vh() - rect.bottom + "px",
                    left: "auto",
                },
            },
            dropend: {
                start: {
                    top: rect.top + "px",
                    right: "auto",
                    bottom: "auto",
                    left: rect.right + offset + "px",
                },
                end: {
                    top: "auto",
                    right: "auto",
                    bottom: dom.vh() - rect.bottom + "px",
                    left: rect.right + offset + "px",
                },
            },
        };
    }

    /**
     * Position the dropdown relative to the toggle element
     * @param {HTMLElement} toggle
     * @param {HTMLElement} dropdown
     * @param {String} type
     * @param {String} anchor
     * @returns
     */
    static relativePosition(toggle, dropdown, type, anchor) {
        const tRect = toggle.getBoundingClientRect();
        const positionOpts = Dropdown.relativePositionOptions(tRect);

        // If type and anchor are valid, return that position
        if (
            Dropdown.types.includes(type) &&
            Dropdown.anchors.includes(anchor)
        ) {
            return positionOpts[type][anchor];
        }

        // Constraints
        const typeOpts = Dropdown.types.includes(type)
            ? [type]
            : Dropdown.types;
        const anchorOpts = Dropdown.anchors.includes(anchor)
            ? [anchor]
            : Dropdown.anchors;
        const dRect = dropdown.getBoundingClientRect();

        // Find best fit
        for (const t of typeOpts) {
            for (const a of anchorOpts) {
                const pos = positionOpts[t][a];
                if (Dropdown.checkFit(pos, dRect.width, dRect.height)) {
                    return pos;
                }
            }
        }

        // Fallback if no fit found (default to first option or requested)
        return positionOpts[typeOpts[0]][anchorOpts[0]];
    }

    /**
     * Position the dropdown relative to the mouse cursor
     * @param {HTMLElement} dropdown
     * @param {Number} mouseX
     * @param {Number} mouseY
     * @returns
     */
    static position(dropdown, mouseX, mouseY) {
        const rect = dropdown.getBoundingClientRect();
        const spaceTop = mouseY;
        const spaceRight = dom.vw() - mouseX;
        const spaceBottom = dom.vh() - mouseY;
        const spaceLeft = mouseX;

        // Determine horizontal anchor (left or right edge of dropdown)
        let left, right;
        if (spaceRight >= rect.width || spaceLeft < rect.width) {
            // Anchor left (dropdown extends right) - DEFAULT
            // Used when there's enough space on right OR not enough space on either side
            left = mouseX;
            right = "auto";
        } else {
            // Anchor right (dropdown extends left)
            // Only used when there's not enough space on right BUT enough on left
            left = "auto";
            right = dom.vw() - mouseX;
        }

        // Determine vertical anchor (top or bottom edge of dropdown)
        let top, bottom;
        if (spaceBottom >= rect.height || spaceTop < rect.height) {
            // Anchor top (dropdown extends down) - DEFAULT
            // Used when there's enough space below OR not enough space on either side
            top = mouseY;
            bottom = "auto";
        } else {
            // Anchor bottom (dropdown extends up)
            // Only used when there's not enough space below BUT enough above
            top = "auto";
            bottom = dom.vh() - mouseY;
        }

        return {
            top: top !== "auto" ? top + "px" : top,
            right: right !== "auto" ? right + "px" : right,
            bottom: bottom !== "auto" ? bottom + "px" : bottom,
            left: left !== "auto" ? left + "px" : left,
        };
    }

    /**
     * @param {MouseEvent} event
     * @param {String} dropdownId
     * @param {Boolean} relative
     * @param {String} type dropdown, dropup, dropstart, dropend
     * @param {String} anchor start, end
     * @returns {void}
     */
    static openDropdown(
        event,
        dropdownId,
        relative = true,
        type = null,
        anchor = null,
    ) {
        // If dropdown visible, let it close first
        if (dom.data().wabDropdownOpen == dropdownId) return;

        const dropdown = dom.id(dropdownId);
        if (!dropdown) return;

        const pos = relative
            ? Dropdown.relativePosition(
                  event.currentTarget,
                  dropdown,
                  type,
                  anchor,
              )
            : Dropdown.position(dropdown, event.clientX, event.clientY);

        dropdown.style.position = "fixed";
        dropdown.style.top = pos.top;
        dropdown.style.right = pos.right;
        dropdown.style.bottom = pos.bottom;
        dropdown.style.left = pos.left;

        // Open dropdwon
        dropdown.classList.add("wab-dropdown-opening");
        setTimeout(() => {
            dropdown.classList.remove("wab-dropdown-opening");
            dropdown.classList.add("wab-dropdown-open");
            dom.data().wabDropdownOpen = dropdownId;
        }, Dropdown.duration);
    }
}

export function openDropdown(
    event,
    dropdownId,
    relative = true,
    type = null,
    anchor = null,
) {
    Dropdown.openDropdown(event, dropdownId, relative, type, anchor);
}

export function closeDropdown(dropdownId) {
    Dropdown.closeDropdown(dropdownId);
}

export class Modal {
    /**
     * Track open modals' id values for Escape key handling
     */
    static stack = [];

    /**
     * Stores each modal's metadata
     */
    static meta = {};

    /**
     * Default CSS z-index for one modal
     */
    static zIndex = 1999;

    /**
     * Transition duration (in milliseconds)
     */
    static duration = 300;

    static {
        if (!dom.data().wabModalInitialized) {
            dom.data().wabModalInitialized = "true";

            // Global Escape key listener
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape" && Modal.stack.length > 0) {
                    const topModalId = Modal.stack[Modal.stack.length - 1];
                    if (Modal.meta[topModalId]?.closeable === true) {
                        Modal.close(topModalId);
                    }
                }
            });

            // Global Backdrop click listener (Event Delegation)
            document.addEventListener("click", (e) => {
                if (e.target.classList.contains("wab-modal-backdrop")) {
                    const id = e.target.id;
                    if (id && Modal.meta[id]?.closeable === true) {
                        Modal.close(id);
                    }
                }
            });
        }
    }

    /**
     * Open static modal (not closed by backdrop click or Escape key)
     * @param {String} id - The ID of the modal to open.
     * @param {Object} options - Options for opening the modal.
     * @param {boolean} options.focus - Whether to focus the modal.
     * @param {boolean} options.closeable - Whether the modal can be closed by backdrop click or Escape key.
     * @param {*} options.data - Data to pass to the modal.
     * @param {number} options.duration - Duration of the modal opening animation.
     */
    static open(
        id,
        {
            focus = true,
            closeable = true,
            data = null,
            duration = Modal.duration,
        } = {},
    ) {
        const backdrop = dom.id(id);
        if (
            !backdrop ||
            !backdrop.classList.contains("wab-modal-backdrop") ||
            isPlainObject(Modal.meta[id])
        )
            return;

        dom.fire("wab.modal.open", { id, data });

        Modal.meta[id] = { focus, closeable, duration, data };
        backdrop.style.zIndex = Modal.zIndex + Modal.stack.length;
        Modal.stack.push(id);

        const modal = backdrop.querySelector(".wab-modal");
        if (duration !== Modal.duration) {
            backdrop.style.transition = `opacity ${duration}ms ease, visibility ${duration}ms ease`;
            modal.style.transition = `transform ${duration}ms ease`;
        }

        backdrop.classList.add("wab-modal-open");
        document.body.style.overflow = "hidden";

        setTimeout(() => {
            if (focus) {
                const target = modal.querySelector("[autofocus]");
                if (target) {
                    target.focus();
                } else {
                    const focusable = modal.querySelectorAll(
                        'input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])',
                    );
                    if (focusable.length > 0) focusable[0].focus();
                }
            }
            dom.fire("wab.modal.opened", { id, data });
        }, duration);
    }

    /**
     * Open closeable modal (closed by backdrop click or Escape key)
     */
    static closeable(
        id,
        { focus = true, data = null, duration = Modal.duration } = {},
    ) {
        return Modal.open(id, { focus, closeable: true, data, duration });
    }

    /**
     * Close open modal and/or cleanup saved data
     */
    static close(id, meta = null) {
        const backdrop = dom.id(id);
        if (!Modal.meta[id]) return;

        // Merge metadata
        Modal.meta[id]["data"] = {
            ...Modal.meta[id]["data"],
            ...{ __close__: meta },
        };

        const { duration, data } = Modal.meta[id];
        dom.fire("wab.modal.close", { id, data });
        if (backdrop) backdrop.classList.remove("wab-modal-open");

        // Update id stack & restore body scroll
        const index = Modal.stack.indexOf(id);
        if (index > -1) Modal.stack.splice(index, 1);
        if (Modal.stack.length === 0) document.body.style.overflow = "";

        // Cleanup metadata, fire closed event
        const doCleanup = () => {
            delete Modal.meta[id];
            dom.fire("wab.modal.closed", { id, data });
        };

        if (backdrop) {
            setTimeout(doCleanup, duration);
        } else {
            doCleanup(); // Immediate cleanup if DOM is gone
        }
    }

    /**
     * Close all open modals in reverse order (topmost first)
     */
    static closeAll() {
        const stack = [...Modal.stack];
        stack.reverse().forEach((id) => Modal.close(id));
    }

    /**
     * Retrieve data passed to an open modal
     */
    static getData(id) {
        return Modal.meta[id]?.data || null;
    }
}

export class Collapse {
    static duration = 300;

    static toggle(id) {
        const el = dom.id(id);
        if (!el) return;
        if (el.classList.contains("wab-collapse-open")) {
            Collapse.hide(id);
        } else {
            Collapse.show(id);
        }
    }

    static show(id) {
        const el = dom.id(id);
        if (!el || el.classList.contains("wab-collapse-open")) return;

        const orientation =
            el.getAttribute("data-wab-orientation") || "vertical";
        const dimension = orientation === "horizontal" ? "width" : "height";
        const scrollDimension =
            orientation === "horizontal" ? "scrollWidth" : "scrollHeight";

        // Dispatch event
        dom.fire("wab.collapse.show", { id, orientation });

        // Initial setup
        el.classList.add("wab-collapsing");
        if (orientation === "horizontal") {
            el.classList.add("wab-collapse-horizontal");
        }

        // Update parent if it's an accordion item
        const parentItem = el.closest(".wab-accordion-item");
        if (parentItem) parentItem.classList.add("wab-item-expanding");

        const size = el[scrollDimension];

        // Start from zero
        el.style[dimension] = "0px";

        // Force reflow
        void el.offsetHeight;

        // Animate to full size
        el.style[dimension] = size + "px";

        setTimeout(() => {
            el.classList.remove("wab-collapsing");
            if (parentItem) parentItem.classList.remove("wab-item-expanding");
            el.classList.add("wab-collapse-open");
            el.style[dimension] = "";
            dom.fire("wab.collapse.shown", { id, orientation });
        }, Collapse.duration);
    }

    static hide(id) {
        const el = dom.id(id);
        if (!el || !el.classList.contains("wab-collapse-open")) return;

        const orientation =
            el.getAttribute("data-wab-orientation") || "vertical";
        const dimension = orientation === "horizontal" ? "width" : "height";
        const scrollDimension =
            orientation === "horizontal" ? "scrollWidth" : "scrollHeight";

        dom.fire("wab.collapse.hide", { id, orientation });

        // Measure starting point
        el.style[dimension] = el[scrollDimension] + "px";

        // Force reflow
        void el.offsetHeight;

        // Prepare for collapsing transition
        el.classList.add("wab-collapsing");
        if (orientation === "horizontal") {
            el.classList.add("wab-collapse-horizontal");
        }
        el.classList.remove("wab-collapse-open");
        el.style[dimension] = "0px";

        setTimeout(() => {
            el.classList.remove("wab-collapsing");
            if (orientation === "horizontal") {
                el.classList.remove("wab-collapse-horizontal");
            }
            el.style[dimension] = "";
            dom.fire("wab.collapse.hidden", { id, orientation });
        }, Collapse.duration);
    }
}

export function toggleCollapse(id) {
    Collapse.toggle(id);
}

export function openCollapse(id) {
    Collapse.show(id);
}

export function closeCollapse(id) {
    Collapse.hide(id);
}

export class Accordion {
    /**
     * Toggle accordion item
     * @param {string} accordionId
     * @param {string} itemId
     */
    static toggle(accordionId, itemId) {
        const accordion = dom.id(accordionId);
        if (!accordion) return;

        const exclusive =
            accordion.getAttribute("data-wab-exclusive") !== "false";

        if (exclusive) {
            const items = accordion.querySelectorAll(".wab-collapse-open");
            items.forEach((item) => {
                if (item.id !== itemId) {
                    Collapse.hide(item.id);
                }
            });
        }

        Collapse.toggle(itemId);
    }
}

export function toggleAccordion(accordionId, itemId) {
    Accordion.toggle(accordionId, itemId);
}

export class Tooltip {
    static types = ["top", "bottom", "left", "right"];
    static active = null;
    static offset = 10;

    static {
        if (!dom.data().wabTooltipInitialized) {
            dom.data().wabTooltipInitialized = "true";

            const show = (e) => {
                const target = e.target.closest("[data-wab-tooltip]");
                if (target) {
                    Tooltip.show(target);
                }
            };

            const hide = (e) => {
                const target = e.target.closest("[data-wab-tooltip]");
                if (target) {
                    Tooltip.hide();
                }
            };

            document.addEventListener("mouseover", show);
            document.addEventListener("mouseout", hide);
            document.addEventListener("focusin", show);
            document.addEventListener("focusout", hide);
        }
    }

    static show(target) {
        const text =
            target.getAttribute("data-wab-tooltip") ||
            target.getAttribute("title");
        if (!text) return;

        if (!Tooltip.active) {
            Tooltip.active = document.createElement("div");
            Tooltip.active.className = "wab-tooltip";
            document.body.appendChild(Tooltip.active);
        }

        Tooltip.active.innerHTML = text;

        const targetRect = target.getBoundingClientRect();
        const tooltipRect = Tooltip.active.getBoundingClientRect();
        const placement = Tooltip.types.includes(
            target.getAttribute("data-wab-placement"),
        )
            ? target.getAttribute("data-wab-placement")
            : "top";

        let top, left;

        switch (placement) {
            case "top":
                top = targetRect.top - tooltipRect.height - Tooltip.offset;
                left =
                    targetRect.left +
                    (targetRect.width - tooltipRect.width) / 2;
                break;
            case "bottom":
                top = targetRect.bottom + Tooltip.offset;
                left =
                    targetRect.left +
                    (targetRect.width - tooltipRect.width) / 2;
                break;
            case "left":
                top =
                    targetRect.top +
                    (targetRect.height - tooltipRect.height) / 2;
                left = targetRect.left - tooltipRect.width - Tooltip.offset;
                break;
            case "right":
                top =
                    targetRect.top +
                    (targetRect.height - tooltipRect.height) / 2;
                left = targetRect.right + Tooltip.offset;
                break;
        }

        // Boundary checks
        if (left < 0) left = 4;
        if (left + tooltipRect.width > dom.vw())
            left = dom.vw() - tooltipRect.width - 4;
        if (top < 0) top = 4;

        Tooltip.active.style.top = `${top + window.scrollY}px`;
        Tooltip.active.style.left = `${left + window.scrollX}px`;
        Tooltip.active.setAttribute("data-wab-placement", placement);
        Tooltip.active.classList.add("wab-tooltip-show");
    }

    static hide() {
        if (Tooltip.active) {
            Tooltip.active.classList.remove("wab-tooltip-show");
        }
    }

    static dispose() {
        Tooltip.active?.remove();
        Tooltip.active = null;
    }
}

export class Draggable {
    static instances = new WeakMap();
    static active = null;
    static offset = 16; // Margin from edges

    // Named snap grid: vertical space split into quarters (25% bands),
    // giving 5 vertical stops per side. Horizontally there are only
    // the two edges - no "center" column - so a dragged widget always
    // ends up pinned to the left or right edge, never free-floating in
    // the middle of the screen where it would sit over content.
    static ROWS = [
        { key: "top", frac: 0 },
        { key: "upper-mid", frac: 0.25 },
        { key: "center", frac: 0.5 },
        { key: "lower-mid", frac: 0.75 },
        { key: "bottom", frac: 1 },
    ];

    static COLS = [
        { key: "left", frac: 0 },
        { key: "right", frac: 1 },
    ];

    static {
        if (!dom.data().wabDraggableInitialized) {
            dom.data().wabDraggableInitialized = "true";

            const move = (e) => {
                if (Draggable.active) Draggable.active.drag(e);
            };

            const end = (e) => {
                if (Draggable.active) {
                    Draggable.active.dragEnd(e);
                    Draggable.active = null;
                }
            };

            document.addEventListener("mousemove", move);
            document.addEventListener("mouseup", end);
            document.addEventListener("touchmove", move, { passive: false });
            document.addEventListener("touchend", end);
        }
    }

    /**
     * @param {HTMLElement} element
     * @param {Object} options
     * @param {boolean} [options.snap=true]
     * @param {Function} [options.onDragStart]
     * @param {Function} [options.onDragEnd]
     */
    constructor(element, options = {}) {
        this.el = element;
        this.options = {
            snap: true,
            onDragStart: null,
            onDragEnd: null,
            ...options,
        };

        this.isDragging = false;
        this.startX = 0;
        this.startY = 0;
        this.currentX = 0;
        this.currentY = 0;
        this.initialX = 0;
        this.initialY = 0;

        this.setupStyles();
    }

    setupStyles() {
        this.el.style.position = "fixed";
        this.el.style.touchAction = "none";
        this.el.style.userSelect = "none";
        this.el.style.zIndex = "3000";
    }

    /**
     * Static entry point for OJS components
     * @param {HTMLElement} element
     * @param {Object} options
     * @param {boolean} [options.snap=true]
     * @param {Function} [options.onDragStart]
     * @param {Function} [options.onDragEnd]
     */
    static init(el, options = {}) {
        if (el.dataset.wabDraggable) return Draggable.instances.get(el);

        const instance = new Draggable(el, options);
        el.dataset.wabDraggable = "true";
        Draggable.instances.set(el, instance);

        // Initial snap - defaults to "bottom-right" but callers can pick
        // any Draggable.grid() point (e.g. "center-right") via
        // options.initialSnap, e.g. for a widget that shouldn't start
        // tucked in the corner.
        const initialPoint = options.initialSnap || "bottom-right";
        requestAnimationFrame(() => instance.snapTo(initialPoint, false));

        return instance;
    }

    /**
     * Inline event handler
     * @param {Event} e
     * @param {Object} options
     * @param {boolean} [options.snap=true]
     * @param {Function} [options.onDragStart]
     * @param {Function} [options.onDragEnd]
     */
    static startDrag(e, options = {}) {
        const el = e.currentTarget;
        let instance = Draggable.instances.get(el);

        if (!instance) {
            instance = Draggable.init(el, options);
        }

        Draggable.active = instance;
        instance.dragStart(e);
    }

    /**
     * Start dragging
     * @param {Event} e
     */
    dragStart(e) {
        if (e.type === "touchstart") {
            this.startX = e.touches[0].clientX;
            this.startY = e.touches[0].clientY;
        } else {
            this.startX = e.clientX;
            this.startY = e.clientY;
        }

        const rect = this.el.getBoundingClientRect();
        this.initialX = rect.left;
        this.initialY = rect.top;

        this.isDragging = true;
        this.el.classList.add("wab-dragging");
        this.el.style.transition = "none";

        if (this.options.onDragStart) this.options.onDragStart(this);
    }

    /**
     * Dragging
     * @param {Event} e
     */
    drag(e) {
        if (!this.isDragging) return;

        if (e.cancelable) e.preventDefault();

        let clientX, clientY;
        if (e.type === "touchmove") {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        const dx = clientX - this.startX;
        const dy = clientY - this.startY;

        this.currentX = this.initialX + dx;
        this.currentY = this.initialY + dy;

        // Constrain to viewport
        const rect = this.el.getBoundingClientRect();
        const vw = dom.vw();
        const vh = dom.vh();

        this.currentX = Math.max(0, Math.min(this.currentX, vw - rect.width));
        this.currentY = Math.max(0, Math.min(this.currentY, vh - rect.height));

        this.el.style.left = `${this.currentX}px`;
        this.el.style.top = `${this.currentY}px`;
        this.el.style.bottom = "auto";
        this.el.style.right = "auto";
    }

    dragEnd() {
        if (!this.isDragging) return;

        this.isDragging = false;
        this.el.classList.remove("wab-dragging");
        this.el.style.transition =
            "all 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28)";

        if (this.options.snap) {
            this.snap();
        }

        if (this.options.onDragEnd) this.options.onDragEnd(this);
    }

    /**
     * Build every named grid point (row-key "-" col-key, e.g.
     * "upper-mid-left") for the given element size. Offset-aware: at
     * frac 0/1 this lands exactly on the old fixed-offset edges, at
     * frac 0.5 it lands exactly on the old centered midpoint, so the
     * original 6 named points are unchanged - this just fills in the
     * quarter/half points between them.
     * @param {{width: number, height: number}} rect
     */
    static grid(rect) {
        const vw = dom.vw();
        const vh = dom.vh();
        const offset = Draggable.offset;

        const availX = Math.max(0, vw - rect.width - 2 * offset);
        const availY = Math.max(0, vh - rect.height - 2 * offset);

        const points = {};

        for (const row of Draggable.ROWS) {
            for (const col of Draggable.COLS) {
                points[`${row.key}-${col.key}`] = {
                    x: offset + col.frac * availX,
                    y: offset + row.frac * availY,
                };
            }
        }

        return points;
    }

    snap() {
        // Read the element's real current position from the DOM rather
        // than this.currentX/this.currentY - those are only ever kept
        // up to date by drag() during an active pointer-move drag, and
        // are never synced by snapTo() (used for every programmatic
        // reposition: the initial placement, resnap() on content-size
        // changes, etc). A widget that has never been manually dragged
        // therefore has currentX/currentY stuck at their constructor
        // default of 0 - snapping against that stale (0, 0) anchor
        // always resolves to the top-left grid point, regardless of
        // where the element actually is on screen. getBoundingClientRect()
        // is always ground truth, during a real drag or not.
        const rect = this.el.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const snapPoints = Draggable.grid(rect);

        let closestPoint = "bottom-right";
        let minDistance = Infinity;

        for (const [key, point] of Object.entries(snapPoints)) {
            const dx = centerX - (point.x + rect.width / 2);
            const dy = centerY - (point.y + rect.height / 2);
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < minDistance) {
                minDistance = distance;
                closestPoint = key;
            }
        }

        this.snapTo(closestPoint);
    }

    /**
     * Snap to a specific named grid point - any "<row>-<col>" combination
     * of Draggable.ROWS (top, upper-mid, center, lower-mid, bottom) and
     * Draggable.COLS (left, right - edges only, no horizontal center),
     * e.g. "top-left", "upper-mid-right", "center-right", "lower-mid-left".
     * @param {string} point
     * @param {boolean} [animate=true]
     */
    snapTo(point, animate = true) {
        const rect = this.el.getBoundingClientRect();
        const target = Draggable.grid(rect)[point];

        if (!target) return;

        if (!animate) this.el.style.transition = "none";

        this.el.style.top = `${target.y}px`;
        this.el.style.left = `${target.x}px`;

        if (!animate) {
            void this.el.offsetHeight;
            this.el.style.transition = "";
        }
    }

    /**
     * Create a new draggable instance
     * @param {HTMLElement} element
     * @param {Object} options
     * @param {boolean} [options.snap=true]
     * @param {Function} [options.onDragStart]
     * @param {Function} [options.onDragEnd]
     */
    static create(element, options) {
        return Draggable.init(element, options);
    }
}

/**
 * Inline event handler
 * @param {Event} e
 * @param {Object} options
 * @param {boolean} [options.snap=true]
 * @param {Function} [options.onDragStart]
 * @param {Function} [options.onDragEnd]
 */
export function handleDragStart(e, options) {
    Draggable.startDrag(e, options);
}

/**
 * Tools class contains utility functions
 */
export class tool {
    static ModalOptions = class ModalOptions {
        buttons = {
            left: {
                callback: () => {},
                text: "Close",
                closeOnClick: true,
                visible: true,
            },

            right: {
                callback: () => {},
                text: "Submit",
                closeOnClick: false,
                visible: true,
            },
        };

        classes = {
            modal: "",
            dialog: "",
            content: "",
            body: "",
            header: "",
            footer: "",
            buttons: {
                left: "btn-outline-secondary",
                right: "btn-dark",
            },
        };

        backDrop = "";
        setters = false;
        removeOnClose = true;

        staticBackdrop() {
            this.backDrop = "static";
            return this;
        }

        noBackdrop() {
            this.backDrop = "";
            return this;
        }
    };

    static emailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    constructor() {}

    static makeGlobal(...args) {
        for (let item of args) {
            let name = null;
            let entity = item;

            if (Array.isArray(item)) {
                entity = item[0];
                name = item[1];
            }

            if (
                (typeof entity === "function" || typeof entity === "object") &&
                entity !== null
            ) {
                // For classes and functions
                if (typeof item === "function") {
                    if (!name) {
                        name = Function.prototype.hasOwnProperty.call(
                            entity,
                            "name",
                        )
                            ? Function.prototype.toString
                                  .call(entity)
                                  .match(/(?:class|function)\s+([^\s({]+)/)?.[1]
                            : entity.name;
                    }
                }
                // For object instances, use constructor
                else if (
                    typeof entity === "object" &&
                    entity.constructor &&
                    !name
                ) {
                    name = item.constructor.name;
                }

                if (name) {
                    if (name != "app") {
                        app().value(name, entity);
                    }
                    window[name] = entity;

                    if (tool.isLocal()) {
                        console.log(`[makeGlobal] app('${name}') registered.`);
                    }
                } else {
                    if (tool.isLocal()) {
                        console.warn(
                            "[makeGlobal] Skipped unnamed item:",
                            entity,
                        );
                    }
                }
            }
        }
    }

    /**
     * Builds  **FormData** Object from a JSON object
     * @param {object} data
     * @returns
     */
    static formData(data) {
        let formData = new FormData();

        for (const key in data) {
            formData.append(key, data[key]);
        }

        return formData;
    }

    /**
     *
     * @param {string} url
     * @returns
     */
    static redirect(url) {
        return (window.location.href = url);
    }

    /**
     * Reloads the page
     */
    static reload() {
        return window.location.reload();
    }

    /**
     * Goes back or forward certain levels
     * @param {number} level
     */
    static back(level = 0) {
        if (level !== 0) {
            return window.history.go(level);
        }

        return window.history.back();
    }

    /**
     * Converts text to JSON
     * @param {string} text
     * @returns
     */
    static json(text) {
        return JSON.parse(text);
    }

    /**
     * Converts an array to Object
     * @param {any[]} arr
     * @returns
     */
    static toObject(arr) {
        let rv = {};
        for (const k in arr) {
            if (arr[k]) rv[k] = arr[k];
        }
        return rv;
    }

    /**
     * Takes a url, updates the query string, and returns the updated url.
     * @author https://stackoverflow.com/users/822711/popnoodles
     * @param {string} key key to search for in the query string. If the key doesn't exist, it will be added.
     * @param {string} value new value to give that key. If null,
     * the key will be removed from the url
     * @param {string} url the url to update. defaults to the current url is left empty.
     *
     * @return {string} updated url
     */
    static url(key, value, url = null) {
        if (!url) url = window.location.href;
        var re = new RegExp("([?&])" + key + "=.*?(&|#|$)(.*)", "gi"),
            hash;

        if (re.test(url)) {
            if (typeof value !== "undefined" && value !== null) {
                return url.replace(re, "$1" + key + "=" + value + "$2$3");
            } else {
                hash = url.split("#");
                url = hash[0].replace(re, "$1$3").replace(/(&|\?)$/, "");
                if (typeof hash[1] !== "undefined" && hash[1] !== null) {
                    url += "#" + hash[1];
                }
                return url;
            }
        } else {
            if (typeof value !== "undefined" && value !== null) {
                var separator = url.indexOf("?") !== -1 ? "&" : "?";
                hash = url.split("#");
                url = hash[0] + separator + key + "=" + value;
                if (typeof hash[1] !== "undefined" && hash[1] !== null) {
                    url += "#" + hash[1];
                }
                return url;
            } else {
                return url;
            }
        }
    }

    /**
     * Converts JSON object to url query string
     * @param {object} data
     * @returns
     */
    static toQueryString(data) {
        let qs = "";
        for (const key in data) {
            if (qs != "") qs += "&";
            qs += `${key}=${data[key]}`;
        }

        return qs;
    }

    static toClipboard(text) {
        if (!navigator.clipboard) {
            tool.fallbackToClipboard(text);
            return;
        }
        navigator.clipboard.writeText(text).then(
            function () {
                inform("Copied", STRINGS.INFO);
            },
            function (err) {
                inform("Unable to copy", STRINGS.WARNING);
            },
        );
    }

    static fallbackToClipboard(text) {
        try {
            let textbox = document.createElement("input");
            textbox.value = text;
            document.body.appendChild(textbox);
            textbox.select();
            document.execCommand("copy");
            document.body.removeChild(textbox);
            inform("Copied", STRINGS.INFO);
        } catch (e) {
            inform("Unable to copy", STRINGS.WARNING);
        }
    }

    static isLocal() {
        return Boolean(
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1" ||
            // ::1 is IPv6 localhost
            window.location.hostname === "::1" ||
            // 192.168.x.x or 10.x.x.x or 172.16.x.x – private IP ranges
            /^192\.168\./.test(window.location.hostname) ||
            /^10\./.test(window.location.hostname) ||
            /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(window.location.hostname),
        );
    }

    /**
     * Create a Modal in realtime.
     * When the buttons callbacks are called, they are passed
     * the unique ID of the modal. use customModal-${uniqueId}
     * to get the modal
     * @param {string} title
     * @param {string} content
     * @param {object<>} options - default
     * @return uniqueId or Object;
     */

    static modal(
        title,
        content,
        options = {
            buttons: {
                left: {
                    callback: () => {},
                    text: "Close",
                    closeOnClick: true,
                    visible: true,
                },

                right: {
                    callback: () => {},
                    text: "Submit",
                    closeOnClick: false,
                    visible: true,
                },
            },
            classes: {
                modal: "",
                dialog: "",
                content: "",
                body: "",
                header: "",
                footer: "",
                buttons: {
                    left: "btn-outline-secondary",
                    right: "btn-dark",
                },
            },
            backDrop: "",
            setters: false,
            removeOnClose: true,
        },
    ) {
        let dataBackdrop =
            options.backDrop == ""
                ? {}
                : { data_bs_backdrop: options.backDrop };
        const h = app("h");
        let uniqueId = new Date().getTime();
        let html = h.div(
            {
                class: `modal fade ${options?.classes?.modal}`,
                id: `customModal-${uniqueId}`,
                tabindex: "-1",
                aria_labelledby: `customModal-${uniqueId}Label`,
                aria_hidden: true,
            },
            dataBackdrop,
            h.div(
                {
                    class: `modal-dialog modal-dialog-centered modal-dialog-scrollable ${options?.classes?.dialog}`,
                },

                h.div(
                    {
                        class: `modal-content border-0 ${options?.classes?.content}`,
                    },

                    h.div(
                        {
                            class: `modal-header border-0 ${options?.classes?.header}`,
                        },

                        h.h5(
                            {
                                class: "modal-title",
                                id: `customModalTitle-${uniqueId}`,
                            },
                            title,
                        ),
                        h.button({
                            type: "button",
                            class: "btn-close",
                            id: `xCloseCustomModal-${uniqueId}`,
                            data_bs_dismiss: "modal",
                            aria_label: "Close",
                        }),
                    ),

                    h.div(
                        {
                            class: `modal-body cm-scrollbar ${options?.classes?.body}`,
                            id: `customModalBody-${uniqueId}`,
                        },
                        content,
                    ),

                    h.div(
                        {
                            class: `modal-footer border-0 ${options?.classes?.footer}`,
                        },

                        h.button(
                            {
                                type: "button",
                                class: `btn btn-modal-close ${
                                    options?.classes?.buttons?.left
                                } ${options.buttons.left.visible ? "" : "d-none"}`,
                                id: `closeCustomModal-${uniqueId}`,
                            },
                            options.buttons.left.closeOnClick !== false
                                ? { data_bs_dismiss: "modal" }
                                : {},
                            options.buttons.left.text,
                        ),

                        h.button(
                            {
                                type: "button",
                                class: `btn ${options?.classes?.buttons?.right} ${
                                    options.buttons.right.visible
                                        ? ""
                                        : "d-none"
                                }`,
                                id: `submitCustomModal-${uniqueId}`,
                            },
                            options.buttons.right.closeOnClick !== false
                                ? { data_bs_dismiss: "modal" }
                                : {},
                            options.buttons.right.text,
                        ),
                    ),
                ),
            ),
        );

        let rootDiv = h.div(html, {
            id: `customModalRoot-${uniqueId}`,
            parent: dom.id("canvas-root"),
        });

        let modal = new bootstrap.Modal(dom.id(`customModal-${uniqueId}`), {
            keyboard: false,
        });

        let rButton = rootDiv.querySelector(`#submitCustomModal-${uniqueId}`);
        let lButton = rootDiv.querySelector(`#closeCustomModal-${uniqueId}`);
        let xButton = rootDiv.querySelector(`#xCloseCustomModal-${uniqueId}`);

        rButton.addEventListener("click", (e) => {
            options.buttons.right.callback(uniqueId);
        });

        lButton.addEventListener("click", (e) => {
            options.buttons.left.callback(uniqueId);
        });

        modal.show();

        $(`#customModal-${uniqueId}`).on("hidden.bs.modal", function () {
            if (options.removeOnClose) {
                rootDiv.remove();
            }
        });

        const setModalTitle = (title) => {
            dom.id(`customModalTitle-${uniqueId}`).innerHTML = title;
        };

        const setModalBody = (body) => {
            dom.id(`customModalBody-${uniqueId}`).innerHTML = body;
        };

        const setRightBtnText = (text) => {
            dom.id(`submitCustomModal-${uniqueId}`).innerHTML = text;
        };

        const setLeftBtnText = (text) => {
            dom.id(`closeCustomModal-${uniqueId}`).innerHTML = text;
        };

        const closeModal = () => {
            xButton.click();
        };

        const destroy = () => {
            modal.destroy();
        };

        return {
            modal: {
                object: modal,
                setTitle: setModalTitle,
                setBody: setModalBody,
                body: () => dom.id(`customModalBody-${uniqueId}`),
                id: uniqueId,
                close: closeModal,
                destroy,
                element: rootDiv,
            },

            buttons: {
                right: {
                    setText: setRightBtnText,
                    button: rButton,
                },
                left: {
                    setText: setLeftBtnText,
                    button: lButton,
                },
                x: {
                    button: xButton,
                },
            },
        };
    }

    static offcanvas(
        body = null,
        head = null,
        position = "bottom",
        attributes = {},
    ) {
        let root = dom.id("canvas-root");

        let rounded = "rounded-top-5";

        switch (position) {
            case "top":
                rounded = "rounded-bottom-5";
                break;
        }

        let border = "border-0";
        if (!this.empty(attributes)) border = "";

        const h = app("h");
        let elem = h.div(
            {
                class: `offcanvas offcanvas-${position} ${rounded} ${border}`,
                tabindex: "-1",
                parent: root,
            },
            attributes,
            head,
            body,
        );

        let cnvs = new bootstrap.Offcanvas(elem);
        return cnvs;
    }

    static plainModal(
        body = null,
        attributes = { data_bs_backdrop: "static" },
        ...args
    ) {
        let root = dom.id("canvas-root");
        const h = app("h");

        let elem = h.div(
            {
                class: `modal fade `,
                tabindex: "-1",
                parent: root,
            },
            attributes,
            h.div(
                {
                    class: "modal-dialog modal-dialog-centered modal-dialog-scrollable",
                },
                body,
                ...args,
            ),
        );

        return new bootstrap.Modal(elem);
    }

    /**
     *
     * @param {Object<>} object
     * @returns {Object<>} new object
     */
    static deepCopy(object) {
        return JSON.parse(JSON.stringify(object));
    }

    /**
     * Increments an HTML element value
     * @param {HTMLElement} element
     */
    static increment(element, max) {
        let newValue = parseInt(element.value) + 1;
        if (newValue > parseInt(max)) newValue = max;
        element.value = newValue;
    }

    /**
     * Decrements an input value until 0
     * @param {HTMLElement} element
     */
    static decrement(element) {
        let value = parseInt(element.value) - 1;
        if (value < 0) value = 0;
        element.value = value;
    }

    /**
     * Gets a value from the element dataset
     *
     * @param {HTMLElement} elem
     * @param {string} key
     * @param {*} def
     */
    static fromDataset(elem, key, def = null) {
        return elem.dataset[key] ?? def;
    }

    /**
     * Gets a query string value from the URL
     * @param {string} key
     * @param {*} def
     * @returns
     */
    static fromUrl(key, def = null, url = null) {
        let u = document.location;
        if (url) u = new URL(url);
        let params = new URLSearchParams(u.search);

        return params.get(key) ?? def;
    }

    /**
     * Checks if a string is empty
     * @param {string} str
     */
    static empty(str) {
        return /^[\s\n\t\r]+$/.test(str) || str.length == 0;
    }

    /**
     * Converts all \n character to <br>
     * @param {string} str
     */
    static enterToBr(str) {
        return str.replace(/\n+/g, "<br/>");
    }
}

/**
 * Event Emitter allows you to use behavior based design pattern
 */
export class FSEventEmitter {
    listeners = {};

    /**
     * Add Event Listener
     * @param {string} eventName the event to listen for
     * @param {Function} fn handler function
     */
    addListener(eventName, fn) {
        this.listeners[eventName] = this.listeners[eventName] || [];
        this.listeners[eventName].push(fn);
        return this;
    }

    /**
     * Adds Event Listener
     * @param {string} eventName
     * @param {Function} fn
     */
    on(eventName, fn) {
        return this.addListener(eventName, fn);
    }

    /**
     * Adds event listener to be executed once
     * @param {string} eventName
     * @param {Function} fn
     * @returns
     */
    once(eventName, fn) {
        this.listeners[eventName] = this.listeners[eventName] || [];
        const onceWrapper = () => {
            fn();
            this.off(eventName, onceWrapper);
        };
        this.listeners[eventName].push(onceWrapper);
        return this;
    }

    /**
     * Removes and event listener
     * @param {string} eventName
     * @param {Function} fn
     * @returns
     */
    off(eventName, fn) {
        return this.removeListener(eventName, fn);
    }

    /**
     * Removes an event listener
     * @param {string} eventName
     * @param {Function} fn
     * @returns
     */
    removeListener(eventName, fn) {
        let lis = this.listeners[eventName];
        if (!lis) return this;
        for (let i = lis.length; i > 0; i--) {
            if (lis[i] === fn) {
                lis.splice(i, 1);
                break;
            }
        }
        return this;
    }

    /**
     * Fires an event
     * @param {string} eventName
     * @param  {...any} args
     * @return {true} true
     */
    emit(eventName, ...args) {
        let fns = this.listeners[eventName];
        if (!fns) return false;
        fns.forEach((f) => {
            f(...args);
        });
        return true;
    }

    /**
     * Returns the number of listeners for this event
     * @param {string} eventName
     * @returns
     */
    listenerCount(eventName) {
        let fns = this.listeners[eventName] || [];
        return fns.length;
    }

    /**
     * Get raw listeners
     * If the once() event has been fired, then that will not be part of
     * the return array
     *
     * @param {string} eventName
     * @returns
     */
    listeners(eventName) {
        return this.listeners[eventName];
    }
}

/**
 * A simple pipelining class
 */
export class Pipeline {
    /**
     * Filters object
     * @type {Array<Filter>}
     */
    filters = [];

    /**
     * Add a filter to this pipeline
     * @param {...Filter} filters the filter function takes in an object
     * and returns an object with
     * two attributes: `{output: filterOutput, next: true|false}`.
     *
     * @returns {Pipeline}
     */
    add(...filters) {
        for (let filter of filters) {
            this.filters.push(filter);
        }

        return this;
    }

    /**
     * Pass data through the pipeline
     * @param {*} data
     */
    async pass(data) {
        let output = data;
        let next = true;

        for (let f of this.filters) {
            let o = await f.run(output);

            output = o.data;
            next = o.next;

            if (!next) return output;
        }

        return output;
    }

    /**
     * Removes all filters from the pipeline
     */
    reset() {
        this.filters = [];
    }
}

/**
 * The filter class
 */
export class Filter {
    /**
     * The logic to run
     */
    logic;

    /**
     *
     * @param {Function} logic the logic to run
     */
    constructor(logic) {
        this.logic = logic;
    }

    /**
     * Runs the filter and returns a response
     * @param {*} input
     * @returns
     */
    async run(input) {
        let o = await this.logic(input);

        if (!("data" in o) || !("next" in o))
            throw Error(
                `A filter must return an object with output and next property. This filter return:`,
                o,
                ` instead`,
            );

        return o;
    }
    /**
     * Creates the Filter output
     * @param {*} data
     * @param {boolean} next proceed to next filter
     * @returns
     */
    static output(data, next = true) {
        return { data, next };
    }
}

/**
 * Network request class
 */
export class Requester {
    /**
     * @var {string} auth token
     */
    token;

    /**
     * @var {string} base url
     */
    baseUrl;

    /**
     * Default headers
     */
    headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
    };

    /**
     * Controls the aborting of a request
     */
    abortController;

    /**
     * The abort signal
     */
    abortSignal;

    _pipeline = new Pipeline();

    callCount = {};

    /**
     * Request Configs such as mode, cache,
     * credentials, redirect, refererPolicy are placed here
     */
    requestConfigs = {};

    /**
     * Creates a fota object
     * @param {object<>} config
     */
    constructor(config = { token: null, baseUrl: null }) {
        this.baseUrl = config.baseUrl;
        this.token = config.token || localStorage.getItem("auth_token");
        this.abortController = new AbortController();
        this.abortSignal = this.abortController.signal;
    }

    /**
     * The pipeline through which the response
     * from the request will be passed
     * @param {Pipeline} pipeline
     */
    pipeline(pipeline) {
        this._pipeline = pipeline;
        return this;
    }

    /**
     * Adds a default Filter to the pipeline
     */
    defaultPipeline() {
        this._pipeline.add(
            new Filter(
                /**
                 *
                 * @param {Response} response
                 * @returns
                 */
                async function (response) {
                    if (!response.ok) {
                        return Filter.output(
                            { status: "error", message: response.statusText },
                            false,
                        );
                    }

                    let data = await response.json();

                    return Filter.output(data, true);
                },
            ),
        );

        return this;
    }

    /**
     *
     * Gets a new requester object with an empty Pipeline
     */
    noPipeline() {
        let r = new Requester();

        for (let k in this) {
            if (k === "_pipeline") continue;

            r[k] = this[k];
        }

        return r;
    }

    /**
     * Make a POST Request and pass the response through
     * the object's pipeline.
     * @param {string} path
     * @param {object} body
     * @param {object} headers
     */
    async post(path, body, headers = {}) {
        return await this._pipeline.pass(
            await this.fetch(path, "post", body, headers),
        );
    }

    /**
     * makes a GET request. the body object
     * will be automatically converted to
     * a query string
     * @param {string} path
     * @param {object} body
     * @param {object} headers
     * @returns
     */
    async get(path, body = {}, headers = {}) {
        if (!this.callCount[path]) {
            this.callCount[path] = 1;
        } else {
            this.callCount[path]++;
        }

        if (!body) {
            body = {};
        }

        body["_ftsct"] = this.callCount[path];

        let qs = new URLSearchParams(body).toString();

        return await this._pipeline.pass(
            await this.fetch(`${path}?${qs}`, "get", null, headers),
        );
    }

    /**
     * Makes a DELETE request and passes the
     * response through the default object's pipeline pipeline
     * @param {string} path
     * @param {object} body
     * @param {object} headers
     * @returns
     */
    async delete(path, body, headers = {}) {
        return await this._pipeline.pass(
            await this.fetch(path, "delete", body, headers),
        );
    }

    /**
     * Makes a PUT request and passes the
     * response through the default object's pipeline pipeline
     * @param {string} path
     * @param {object} body
     * @param {object} headers
     * @returns
     */
    async put(path, body, headers = {}) {
        return await this._pipeline.pass(
            await this.fetch(path, "put", body, headers),
        );
    }

    /**
     * The underlying fetch method
     * @param {string} path
     * @param {string} method
     * @param {object} body
     * @param {object} headers
     * @returns
     */
    async fetch(path, method, body, headers) {
        if (window.Echo) {
            headers["X-Socket-ID"] = Echo.socketId();
        }

        let finalHeaders = {
            ...this.headers,
            ...headers,
        };

        if (this.token) {
            finalHeaders["Authorization"] = `Bearer ${this.token}`;
        }

        let finalPath = "";
        path = path.trim();

        if (/^https?:\/\//.test(path)) {
            finalPath = path;
        } else {
            path = path.replace(/\/{2,}/g, "/");
            finalPath = `${this.baseUrl}${path[0] != "/" ? "/" : ""}${path ?? ""}`;
        }

        const options = {
            url: finalPath,
            method: method.toUpperCase(),
            headers: finalHeaders,
        };

        if (body) {
            if (finalHeaders["Content-Type"] === "application/json") {
                options.data = body;
            } else {
                options.data = body;
            }
        }

        const response = await CapacitorHttp.request(options);

        return {
            ok: response.status >= 200 && response.status < 300,
            status: response.status,
            statusText: "",
            json: async () => response.data,
            text: async () =>
                typeof response.data === "string"
                    ? response.data
                    : JSON.stringify(response.data),
            headers: {
                get: (name) =>
                    response.headers[name] ||
                    response.headers[name.toLowerCase()],
            },
            silent: headers.silent ?? false,
        };
    }

    /**
     * Aborts the current network request
     */
    abort() {
        this.abortController.abort();
    }
}

export class IdGenerator {
    #ID = 1;

    getId() {
        return this.#ID++;
    }

    toString() {
        return this.getId();
    }
}
